<script setup lang="ts">
  import { Cross, UiButton } from '@logistiek-zonder-papier/lzp-library'

  type Props = {
    isOpen: boolean
    title?: string
    description?: string
    submitLabel?: string
    submitDisabled?: boolean
    width?: number | string
  }

  defineProps<Props>()
  const emit = defineEmits(['close', 'submit'])

  defineSlots<{
    default: () => unknown
    footer?: () => unknown
  }>()

  function closeModal() {
    emit('close')
  }
</script>

<template>
  <Transition name="sheet">
    <div v-show="isOpen" class="sheet-backdrop" @click.self="closeModal">
      <div class="sheet-content" :style="width ? { width } : undefined">
        <div class="sheet-header">
          <div>
            <p v-if="title" class="sheet-title">{{ title }}</p>
            <p v-if="description" class="sheet-description">{{ description }}</p>
          </div>

          <div class="sheet-close">
            <Cross @click="closeModal" />
          </div>
        </div>

        <div class="sheet-body">
          <slot />
        </div>

        <div class="sheet-footer">
          <slot name="footer">
            <UiButton label="Sluiten" @click="closeModal" />
            <UiButton
              v-if="submitLabel"
              :label="submitLabel"
              variant="prominent"
              :disabled="submitDisabled"
              @click="emit('submit')"
            />
          </slot>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="css" scoped>
  .sheet-backdrop {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 20;
    background-color: rgba(0, 0, 0, 0.7);
    width: 100vw;
    height: 100dvh;
    overflow: hidden;
  }

  .sheet-content {
    display: flex;
    flex-direction: column;
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 40rem;
    background-color: var(--colours-grey-1);
    color: var(--colours-grey-5);
    padding-top: var(--spacing-s);
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);
    padding-bottom: var(--spacing-s);
  }

  .sheet-header {
    display: flex;
    justify-content: space-between;
  }

  .sheet-title {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
  }

  .sheet-description {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--colours-grey-4);
    margin: 0;
  }

  .sheet-close {
    cursor: pointer;
  }

  .sheet-body {
    flex: 1;
    margin: 1rem 0;
    overflow-y: auto;
  }

  .sheet-footer {
    display: flex;
    justify-content: end;
    gap: 0.5rem;
  }

  .sheet-enter-active,
  .sheet-leave-active {
    transition: background-color 150ms ease-in-out;
  }

  .sheet-enter-from,
  .sheet-leave-to {
    background-color: rgba(0, 0, 0, 0);
  }

  .sheet-enter-active .sheet-content,
  .sheet-leave-active .sheet-content {
    transition: transform 150ms ease-in-out;
  }

  .sheet-enter-from .sheet-content,
  .sheet-leave-to .sheet-content {
    transform: translateX(100%);
  }

  @media (prefers-reduced-motion: reduce) {
    .sheet-enter-active,
    .sheet-leave-active,
    .sheet-enter-active .sheet-content,
    .sheet-leave-active .sheet-content {
      transition: none;
    }
  }
</style>
